import React, { useEffect, useState } from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Paper,
  Tooltip,
  IconButton,
  Box,
} from "@mui/material";
import { Loan, LoanType, useDealContext } from "@/contexts/DealContext";
import { Trans } from "@lingui/react/macro";
import { useNavigate } from "react-router";
import DynamicIcon from "../ui/DynamicIcon";
import { getMimeExtension } from "../ui/mimeTypes";

type LoanSetupStepProps = {
  loan: Loan;
};

export const capitalizeFirstLetter = (str: string | null, locale = navigator.language) =>
  str ? str.charAt(0).toLocaleUpperCase(locale) + str.slice(1).toLocaleLowerCase() : "";

const LoanSetupStep = ({ loan }: LoanSetupStepProps) => {
  const { setSubmitFunction, setIsStepValid, updateLoan, setDrawerOpen, drawerOpen } =
    useDealContext();
  const [loanName, setLoanName] = useState<string>(loan?.name || "");
  const [loanDescription, setLoanDescription] = useState<string>(loan?.description || "");
  const [loanType, setLoanType] = useState<LoanType | "">(loan?.type || "");
  const navigate = useNavigate();
  useEffect(() => {
    if (loan) {
      setLoanName(loan.name);
      setLoanDescription(loan.description || "");
    }
  }, [loan]);
  const submit = () => {
    if (loanName.trim() && loanType && loan.id) {
      updateLoan(loan.id, { name: loanName, type: loanType, description: loanDescription });
      navigate(`/review/loan/${loan.id}/amount`);
    }
  };
  useEffect(() => {
    setIsStepValid(!!loanName.trim() && !!loanType);
    setSubmitFunction(() => submit);
  }, [loanName, loanType, setIsStepValid, setSubmitFunction]);

  const formatedMime = (mimeType: string) =>
    getMimeExtension(mimeType) === "?" ? mimeType : getMimeExtension(mimeType);

  return (
    <Paper sx={{ p: 4, mt: 3 }} elevation={0}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="subtitle1" gutterBottom>
          <Trans>
            Enter a <b>name and description</b> of the loan and select the <b>type of loan</b>
          </Trans>
        </Typography>
        <Tooltip title={`Preview of ${loan.file?.name}`}>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <DynamicIcon
              iconName={`File${formatedMime(loan.file?.type as string)}`}
              fallback="FileUnknown"
            />
          </IconButton>
        </Tooltip>
      </Box>
      <TextField
        label="Loan name"
        variant="outlined"
        fullWidth
        value={loanName}
        onChange={(e) =>  {
          setLoanName(e.target.value)
          updateLoan(loan.id, { name: e.target.value })
        }}
        sx={{ mt: 2 }}
      />

      <TextField
        label="Loan description"
        variant="outlined"
        fullWidth
        value={loanDescription}
        onChange={(e) => {
          setLoanDescription(e.target.value)
          updateLoan(loan.id, { description: e.target.value })
        }}
        sx={{ mt: 2 }}
      />

      <FormControl fullWidth sx={{ mt: 3 }}>
        <InputLabel>
          <Trans>Loan type</Trans>
        </InputLabel>
        <Select
          value={loanType}
          onChange={(e) => {
            setLoanType(e.target.value as LoanType)
            updateLoan(loan.id, { type: e.target.value as LoanType })
          }}
          label="Loan type"
        >
          {Object.entries(LoanType).map(([key, value]) => (
            <MenuItem key={key} value={value}>
              {capitalizeFirstLetter(value.replace(/_/g, " "))}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Paper>
  );
};

export default LoanSetupStep;
